import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, IconButton, styled, Typography } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { useStore } from "utils/useStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { AuthStore } from "stores/AuthStore";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { runInAction } from "mobx";
import AcxButton from "components/UI/AcxButton";
import { Add, Cancel, TurnRightRounded } from "@mui/icons-material";
import { FilterLabel } from "components/Conversations/Views/Drawer/shared";
import { SlimLLMClassifierResponse } from "models/LLMs";
import _ from "lodash";
import type { ApplicationFilterOptionRestriction } from "../BaseFilterComponents/IApplicationFilterComponent";
import { useOptionRestriction } from "../BaseFilterComponents/useRestrictedOptions";

const GridWithBackground = styled(Grid)(({ theme }) => ({
    gap: theme.spacing(1),
    flexWrap: "nowrap",
    backgroundColor: theme.palette.lightgray.background,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius / 4,
}));

const FullWidthGrid = styled(Grid)(() => ({ width: "100%" }));

type ModelResultsFilterProps = {
    variant?: "signals" | "conversations";
    labelComponent?: React.ReactNode;
} & IApplicationFilterComponent;

function buildRestrictedResultsByClassifier(
    optionRestriction?: ApplicationFilterOptionRestriction,
): Map<string, Set<string>> {
    const restrictedByClassifier = new Map<string, Set<string>>();

    (optionRestriction?.values ?? []).forEach((entry) => {
        if (typeof entry !== "string") return;
        const [classifierId, result] = entry.split("::");
        if (!classifierId || !result) return;
        if (!restrictedByClassifier.has(classifierId)) {
            restrictedByClassifier.set(classifierId, new Set<string>());
        }
        restrictedByClassifier.get(classifierId)!.add(result);
    });

    return restrictedByClassifier;
}

function isResultAllowed(
    restrictionType: ApplicationFilterOptionRestriction["type"] | undefined,
    restrictedResults: Set<string> | undefined,
    result: string,
): boolean {
    if (!restrictionType) return true;
    if (!restrictedResults) {
        return restrictionType === "denyList";
    }
    return restrictionType === "allowOnly"
        ? restrictedResults.has(result)
        : !restrictedResults.has(result);
}

export const ModelResultsFilter = observer(function ({
    variant = "signals",
    ...props
}: ModelResultsFilterProps) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const authStore = useStore(AuthStore);
    const [isAddingResult, setIsAddingResult] = useState(
        props.store.modelResults === undefined ||
            props.store.modelResults.length === 0,
    );
    const { optionRestriction } = useOptionRestriction({
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
    });
    const restrictionType = optionRestriction?.type;

    // prevent showing the empty selection if we change the filters programmatically (e.g. view in convos)
    useEffect(() => {
        setIsAddingResult(props.store.modelResults?.length === 0);
    }, [props.store.modelResults?.length]);

    const isSignalsVariant = variant === "signals";
    const isConversationsVariant = variant === "conversations";
    const restrictedResultsByClassifier = useMemo(
        () => buildRestrictedResultsByClassifier(optionRestriction),
        [optionRestriction],
    );
    const hasRestrictions = restrictedResultsByClassifier.size > 0;

    useEffect(() => {
        if (!hasRestrictions || !props.store.modelResults) return;

        const sanitizedModelResults = props.store.modelResults
            .map((model) => {
                const classifierId = `${model?.id ?? ""}`;
                const restrictedResults = restrictedResultsByClassifier.get(
                    classifierId,
                );

                if (
                    restrictionType === "allowOnly" &&
                    !restrictedResults
                ) {
                    return null;
                }

                const filteredResults = (model.results ?? []).filter((result) =>
                    isResultAllowed(restrictionType, restrictedResults, result),
                );

                return {
                    ...model,
                    results: filteredResults,
                };
            })
            .filter((model): model is SlimLLMClassifierResponse =>
                Boolean(model),
            );

        if (!_.isEqual(sanitizedModelResults, props.store.modelResults)) {
            props.store.setModelResults(sanitizedModelResults);
        }
    }, [
        restrictedResultsByClassifier,
        hasRestrictions,
        restrictionType,
        props.store,
        props.store.modelResults,
    ]);

    // compute options that user hasn't already selected.
    const modelOptions = useMemo(() => {
        let options = valuesStore.llmClassifiers ?? [];
        if (hasRestrictions) {
            options = options.filter((option) => {
                const restrictedResults = restrictedResultsByClassifier.get(
                    `${option.id}`,
                );

                if (restrictionType === "allowOnly") {
                    return !!restrictedResults;
                }

                if (!restrictedResults) return true;

                return option.results.some(
                    (result) => !restrictedResults.has(result),
                );
            });
        }
        const selectedIds = props.store.modelResults?.map((r) => `${r.id}`);

        if (!selectedIds) return options;

        return options.filter((o) => !selectedIds.includes(`${o.id}`));
    }, [
        restrictedResultsByClassifier,
        hasRestrictions,
        restrictionType,
        props.store.modelResults,
        valuesStore.llmClassifiers,
    ]);
    const canAddMoreModels =
        valuesStore.llmClassifiers === undefined || modelOptions.length > 0;

    useEffect(() => {
        if (!canAddMoreModels && isAddingResult) {
            setIsAddingResult(false);
        }
    }, [canAddMoreModels, isAddingResult]);

    // compute the model results the user hasn't selected for a given
    // llm classifier
    const getResultOptions = useCallback(
        (llmClassifier: SlimLLMClassifierResponse) => {
            const allResultOptions = valuesStore.llmClassifiers?.find(
                (option) => option.id === llmClassifier.id,
            );
            if (!allResultOptions) return [];

            const restrictedResults = restrictedResultsByClassifier.get(
                `${llmClassifier.id}`,
            );

            return allResultOptions.results
                .filter((result) =>
                    hasRestrictions
                        ? isResultAllowed(
                              restrictionType,
                              restrictedResults,
                              result,
                          )
                        : true,
                )
                .filter((result) => !llmClassifier.results.includes(result))
                .map((r) => ({ label: r, value: r }));
        },
        [
            restrictedResultsByClassifier,
            hasRestrictions,
            restrictionType,
            valuesStore.llmClassifiers,
        ],
    );

    const canViewModelResults = authStore.canUserView("Model Results");
    const loadLLMClassifiersIfNotAlreadyLoaded = useCallback(() => {
        if (
            !canViewModelResults ||
            valuesStore.getTaskLoading(
                ApplicationFilterValuesStore.Tasks.LOAD_LLM_CLASSIFIERS,
            ) ||
            !!valuesStore.llmClassifiers
        )
            return;
        valuesStore.loadLLMClassifiers();
    }, [canViewModelResults, valuesStore]);

    useEffect(() => {
        loadLLMClassifiersIfNotAlreadyLoaded();
    }, [loadLLMClassifiersIfNotAlreadyLoaded]);

    if (!canViewModelResults) return null;

    return (
        <Grid id={props.id} container item direction="column" rowGap={1}>
            <Grid
                container
                item
                width="100%"
                flexWrap="nowrap"
                justifyContent="space-between"
                alignItems="center"
                columnGap={1}
            >
                <Grid item>
                    <FilterLabel>Model Results</FilterLabel>
                </Grid>
                {isConversationsVariant && canAddMoreModels && (
                    <Grid item>
                        <AcxButton
                            color="white"
                            onClick={() => setIsAddingResult(true)}
                            sx={(theme) => ({
                                padding: "1px",
                                borderRadius: theme.shape.borderRadius / 4,
                                width: "20px",
                                height: "20px",
                                minWidth: "0px",
                                fontSize: "18px",
                                color: theme.palette.gray[400],
                            })}
                        >
                            <Add fontSize="inherit" />
                        </AcxButton>
                    </Grid>
                )}
            </Grid>
            {valuesStore.llmClassifiers?.length === 0 &&
                !props.store.modelResults?.length && (
                    <Typography variant="body2" color="text.secondary">
                        No published models available to filter.
                    </Typography>
                )}
            {props.store.modelResults?.map((llmClassifier, index) => (
                <GridWithBackground
                    key={llmClassifier.id ?? llmClassifier.name ?? index}
                    container
                    item
                    direction="row"
                    alignItems="flex-start"
                >
                    <FullWidthGrid
                        container
                        item
                        key={llmClassifier.id}
                        direction={isSignalsVariant ? "row" : "column"}
                        gap={1}
                        flexWrap="nowrap"
                    >
                        <FullWidthGrid
                            item
                            onMouseEnter={() =>
                                loadLLMClassifiersIfNotAlreadyLoaded()
                            }
                        >
                            <AcxSelectSingle
                                id={"application-filters-llm-classifier-select"}
                                fullWidth
                                defaultValue={llmClassifier}
                                options={modelOptions}
                                labelField="name"
                                valueField="id"
                                onChange={(item) => {
                                    runInAction(() => {
                                        llmClassifier.id = item.id;
                                        llmClassifier.name = item.name;
                                        llmClassifier.results = [];
                                    });
                                }}
                                isLoading={valuesStore.getTaskLoading(
                                    ApplicationFilterValuesStore.Tasks
                                        .LOAD_LLM_CLASSIFIERS,
                                )}
                            />
                        </FullWidthGrid>
                        <FullWidthGrid
                            container
                            item
                            columnGap={1}
                            flexWrap="nowrap"
                            alignItems="center"
                        >
                            {isConversationsVariant && (
                                <Grid item>
                                    <TurnRightRounded
                                        sx={(theme) => ({
                                            color: theme.palette.gray[400],
                                            transform: "scale(1, -1)",
                                        })}
                                    />
                                </Grid>
                            )}
                            <FullWidthGrid item>
                                <AcxSelectMulti
                                    id="application-filters-llm-classifier-outputs"
                                    fullWidth
                                    defaultValue={llmClassifier.results.map(
                                        (r) => ({
                                            label: r,
                                            value: r,
                                        }),
                                    )}
                                    options={getResultOptions(llmClassifier)}
                                    onChange={(options) => {
                                        runInAction(() => {
                                            if (!options)
                                                llmClassifier.results = [];
                                            else
                                                llmClassifier.results =
                                                    options.map((o) => o.label);
                                        });
                                    }}
                                    containerHeight="fit-content"
                                    maxContainerHeight="128px"
                                    isClearable={true}
                                    valueField={"value"}
                                    labelField={"label"}
                                    placeholder="Select model results..."
                                />
                            </FullWidthGrid>
                        </FullWidthGrid>
                    </FullWidthGrid>
                    <Grid item>
                        <IconButton
                            size="small"
                            onClick={() => {
                                const remainingModelResults =
                                    props.store.modelResults?.filter(
                                        (r) => r.id !== llmClassifier.id,
                                    ) ?? [];
                                props.store.setModelResults(
                                    remainingModelResults,
                                );
                                if (remainingModelResults.length === 0)
                                    setIsAddingResult(true);
                            }}
                        >
                            <Cancel fontSize="small" />
                        </IconButton>
                    </Grid>
                </GridWithBackground>
            ))}
            {isAddingResult && canAddMoreModels && (
                <GridWithBackground
                    container
                    item
                    direction="row"
                    alignItems="flex-start"
                >
                    <FullWidthGrid
                        container
                        item
                        direction={isSignalsVariant ? "row" : "column"}
                        gap={1}
                        flexWrap="nowrap"
                    >
                        <FullWidthGrid
                            item
                            onMouseEnter={() =>
                                loadLLMClassifiersIfNotAlreadyLoaded()
                            }
                        >
                            <AcxSelectSingle
                                id={"application-filters-llm-classifier-select"}
                                fullWidth
                                options={modelOptions}
                                labelField="name"
                                valueField="id"
                                onChange={(item) => {
                                    setIsAddingResult(false);
                                    props.store.setModelResults([
                                        ...(props.store.modelResults ?? []),
                                        {
                                            id: item.id,
                                            name: item.name,
                                            results: [],
                                        },
                                    ]);
                                }}
                                isLoading={valuesStore.getTaskLoading(
                                    ApplicationFilterValuesStore.Tasks
                                        .LOAD_LLM_CLASSIFIERS,
                                )}
                                placeholder="Select model..."
                            />
                        </FullWidthGrid>
                        <FullWidthGrid
                            container
                            item
                            columnGap={1}
                            flexWrap="nowrap"
                            alignItems="center"
                        >
                            {isConversationsVariant && (
                                <Grid item>
                                    <TurnRightRounded
                                        sx={(theme) => ({
                                            color: theme.palette.gray[400],
                                            transform: "scale(1, -1)",
                                        })}
                                    />
                                </Grid>
                            )}
                            <FullWidthGrid item>
                                <AcxSelectMulti
                                    id="application-filters-llm-classifier-outputs"
                                    fullWidth
                                    isDisabled
                                    options={[]}
                                    onChange={() => {}}
                                    isClearable={true}
                                    valueField={""}
                                    labelField={""}
                                    placeholder="Select model results..."
                                />
                            </FullWidthGrid>
                        </FullWidthGrid>
                    </FullWidthGrid>
                    {props.store.modelResults &&
                        props.store.modelResults.length > 0 && (
                            <Grid item>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setIsAddingResult(false);
                                    }}
                                >
                                    <Cancel fontSize="small" />
                                </IconButton>
                            </Grid>
                        )}
                </GridWithBackground>
            )}
            {isSignalsVariant && canAddMoreModels && (
                <AcxButton
                    color="white"
                    startIcon={<Add />}
                    sx={(theme) => ({ color: theme.palette.primary.main })}
                    onClick={() => setIsAddingResult(true)}
                >
                    Model
                </AcxButton>
            )}
        </Grid>
    );
});
